import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { AuthService } from './auth.service';
import { inject } from '@angular/core';
import { catchError, switchMap, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { Globals } from './shared/globals';
import { NotificationService } from './shared/notification.service';
import { AppUpdateService } from './shared/app-update.service';

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const notificationService = inject(NotificationService);
  const appUpdateService = inject(AppUpdateService);
  const authToken = authService.token;
  
  // Skip token validation for auth endpoints (login, refresh, etc.)
  const isAuthEndpoint = req.url.includes('/token') || req.url.includes('/refresh') || req.url.includes('/autologin');
  
  // If token exists and it's not an auth endpoint, check if it's expired and refresh if needed
  if (authToken && !isAuthEndpoint) {
    if (authService.isCurrentTokenExpired()) {
      // Token is expired, refresh it before sending the request
      console.log('Interceptor: Token expired, refreshing...');
      return authService.refreshToken().pipe(
        switchMap(success => {
          if (!success) {
            console.log('Interceptor: Token refresh failed');
            // Refresh failed, return 401 error
            return throwError(() => ({ status: 401, message: 'Token refresh failed' }));
          }
          console.log('Interceptor: Token refresh succeeded, proceeding with request');
          // Refresh succeeded, clone request with new token
          const clonedReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${authService.token}`,
              'x-web-version': Globals.Constants.VERSION_NUMBER
            }
          });
          return next(clonedReq).pipe(
            catchError((error) => {      
              if (error.status === 401) {
                authService.clearLogin();        
              }
              return throwError(() => error);
            }),
            tap((event) => {      
              if (event.type === 4) {
                const minWebVersion = event.headers.get('x-web-minversion');        
                if (minWebVersion) { 
                  if (Number(event.headers.get('x-web-minversion')) > Globals.Constants.VERSION_NUMBER_INT) {
                    appUpdateService.checkForUpdateAndReload();
                    notificationService.showErrorPinned('You are using an outdated version. Please refresh the application.');                        
                  }
                }
              }
            })
          );
        })
      );
    }
    // Token is valid, add it to the request
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${authToken}`,
        'x-web-version': Globals.Constants.VERSION_NUMBER
      }
    });
  }
  return next(req).pipe(
    catchError((error) => {      
      if (error.status === 401) {
        // Handle unauthorized error        
        authService.clearLogin();        
      }
      return throwError(() => error);
    }),
    tap((event) => {      
      if (event.type === 4) { // HttpEventType.Response        
        const minWebVersion = event.headers.get('x-web-minversion');        
        if (minWebVersion) { 
          if (Number(event.headers.get('x-web-minversion')) > Globals.Constants.VERSION_NUMBER_INT) {
            appUpdateService.checkForUpdateAndReload();
            notificationService.showErrorPinned('You are using an outdated version. Please refresh the application.');      
          }
        }
      }
    })
  );
};